<template>
	<div
		v-if="selected_option"
		class="gallery-modal-wrapper"
	>
		<div class="gallery-modal">
			<div class="gallery-modal__header">
				<p class="gallery-modal__count">
					{{ getCount() }}
				</p>
				<div class="gallery-modal__nav-buttons">
					<button
						class="gallery-modal__nav-prev"
						:class="{ 'gallery-modal__nav-prev--disabled': first_selected }"
						:disabled="first_selected"
						@click="viewPrev"
					/>
					<button
						class="gallery-modal__nav-next"
						:class="{ 'gallery-modal__nav-next--disabled': last_selected }"
						:disabled="last_selected"
						@click="viewNext"
					/>
				</div>
				<div>
					<p
						v-if="selected_option.title"
						class="gallery-modal__title"
					>
						{{ selected_option.related_project[0].title }}
					</p>
					<div
						class="gallery-modal__details"
					>
						<p>{{ selected_option.asset_title }}</p>
						<div
							:class="[
								'image-grid-card__stage',
								getRevisionPipClass( selected_option.revision_number )
							]"
						>
							Stage {{ selected_option.revision_number[0] }}
						</div>
						<div class="image-grid-card__stage image-grid-card__stage--approved-option">
							Option {{ selected_option.option_number }}
						</div>
						<p>
							<span>
								{{ getDate( selected_option.postDate ) }}
							</span>
							<span
								class="gallery-modal__details-bullet"
							>
								Created by {{ selected_option.artist?.fullName ?? 'unknown artist' }}
							</span>
						</p>
					</div>
				</div>

				<div class="gallery-modal__buttons">
					<a
						v-if="config.downloadable && selected_option.image_url"
						class="gallery-modal__button"
						:href="selected_option_image_download_url"
					>
						Download
					</a>
				</div>

				<div class="gallery-modal__links">
					<button
						class="a kappa"
						@click="show_tags = !show_tags"
					>
						{{ show_tags == true ? 'Hide tags' : 'View tags' }}
					</button>
					<router-link
						:to="{ name: 'projects__project', params: { project_id: selected_option.related_project[0].id } }"
						class="a kappa"
					>
						Go to project
					</router-link>
					<router-link
						:to="{
							name: 'projects__project__asset',
							params: {
								project_id: selected_option.related_project[0].id,
								asset_id: selected_option.asset_id
							}
						}"
						class="a kappa"
					>
						Go to asset
					</router-link>
					<button
						class="database-grid-card__like-button database-grid-card__like-button--pill"
						:class="[
							liked_options.includes( parseInt( selected_option.id ) ) ? 'database-grid-card__like-button--liked' : 'database-grid-card__like-button--unliked',
							{ 'database-grid-card__like-button--loading': loading_modal_likes }
						]"
						:disabled="loading_modal_likes"
						@click="$emit( 'update-likes', selected_option.id )"
					>
						{{ selected_option.likes ?? 0 }}
					</button>
				</div>

				<button
					class="gallery-modal__close-modal gallery-modal__close-modal--has-links"
					@click="emit( 'close-modal' )"
				/>
			</div>

			<div
				class="gallery-modal__content"
				:class="{ 'gallery-modal__content--show-sidebar': show_tags }"
			>
				<div
					class="gallery-modal__content-wrapper"
					:class="{ 'gallery-modal__content-wrapper--full-resolution': show_full_resolution }"
				>
					<PageLoaderComponent
						v-if="loading"
					/>

					<img
						v-if="selected_option.image_url"
						v-show="!loading"
						class="gallery-modal__image"
						:class="{ 'gallery-modal__image--full-resolution': show_full_resolution }"
						:style="full_resolution_styles"
						:src="selected_option.image_url"
						@load="loading = false"
						@click="toggleFullResolution"
					>
					<video
						v-else-if="selected_option.video_url"
						v-show="!loading"
						class="gallery-modal__video"
						:src="selected_option.video_url"
						:alt="selected_option.title"
						autoplay
						controls
						@loadeddata="loading = false"
					/>
					<iframe
						v-else-if="selected_option.tour_id"
						v-show="!loading"
						class="gallery-modal__explore"
						:src="selected_option.tour_id"
						@load="loading = false"
					/>
				</div>
				<DatabaseGalleryTagsSidebarComponent
					v-if="show_tags"
					:selected-option="selected_option"
					@update-database-tags="updateDatabaseTags"
				/>
			</div>
		</div>
	</div>
</template>

<script setup>
import PageLoaderComponent from '../PageLoaderComponent.vue';
import DatabaseGalleryTagsSidebarComponent from '../sidebars/DatabaseGalleryTagsSidebarComponent';


import moment from 'moment';
import { onMounted, computed, ref, reactive, onUnmounted, watch, watchEffect, defineProps, defineEmits } from 'vue';

const props = defineProps( {
	options: {
		required: true,
		type: Array
	},
	selectedGalleryIndex: {
		required: true,
		type: Number
	},
	likedOptions: {
		required: true,
		type: Array
	},
	loadingModalLikes: {
		type: Boolean,
		required: true
	},
	config: {
		required: false,
		type: Object,
		default: () => {
			return {
				downloadable: false
			};
		}
	}
} );

const emit = defineEmits( [ 'close-modal', 'update-option', 'fetch-more-results', 'select-prev', 'select-next', 'update-likes', 'update-database-tags' ] );

const loading = ref( false );
const show_tags = ref( false );
const loading_modal_likes = ref( props.loadingModalLikes );

onMounted( () => {
	window.addEventListener( 'keydown', handleKeyPress );
} );

onUnmounted( () => {
	window.removeEventListener( 'keydown', handleKeyPress );
} );

const selected_option = computed( () => {
	return props.options[props.selectedGalleryIndex];
} );

const liked_options = computed( () => {
	return props.likedOptions;
} );

const selected_option_image_download_url = computed( () => {
	const escaped_url = encodeURIComponent(
		selected_option.value.image_url
	);

	return `/download-option?url=${escaped_url}`;
} );

const first_selected = computed( () => {
	return props.options[0] === props.options[props.selectedGalleryIndex];
} );

const last_selected = computed( () => {
	return props.options.at( -1 ) === props.options[props.selectedGalleryIndex];
} );

watch( last_selected, ( new_value, old_value ) => {
	if ( old_value === false && new_value === true ) {
		emit( 'fetch-more-results' );
	}
} );

watchEffect( () => {
	loading_modal_likes.value = props.loadingModalLikes;
} );

function handleKeyPress( e ) {
	switch ( e.key ) {
		case 'ArrowRight':
			if ( last_selected.value ) {
				return;
			}
			return viewNext();
		case 'ArrowLeft':
			if ( first_selected.value ) {
				return;
			}
			return viewPrev();
		case 'Escape':
			return emit( 'close-modal' );
		default:
			return;
	}
}

function viewPrev() {
	loading.value = true;
	show_full_resolution.value = false;
	emit( 'select-prev' );
}

function viewNext() {
	loading.value = true;
	show_full_resolution.value = false;
	emit( 'select-next' );
}

// Click to zoom
const show_full_resolution = ref( false );
const full_resolution_coordinates = reactive( {
	x: null,
	y: null
} );

const full_resolution_styles = computed( () => {
	if ( !show_full_resolution.value ) {
		return null;
	}
	return {
		objectPosition: `${full_resolution_coordinates.x} ${full_resolution_coordinates.y}`
	};
} );

function toggleFullResolution( e ) {
	show_full_resolution.value = !show_full_resolution.value;
	if ( !show_full_resolution.value ) {
		full_resolution_coordinates.x = null;
		full_resolution_coordinates.y = null;
	}

	full_resolution_coordinates.x = `${e.offsetX / e.target.offsetWidth * 100}%`;
	full_resolution_coordinates.y = `${e.offsetY / e.target.offsetHeight * 100}%`;
}

function getCount() {
	const current_selection = props.selectedGalleryIndex + 1;
	return `${ current_selection }/${ props.options.length }`;
}

const getDate = ( timestamp ) => {
	return moment( new Date( timestamp ) ).format( 'D/M/YYYY' );
};

// Revision

function getRevisionPipClass( revision_number ) {
	const first_char = revision_number[0];

	switch ( first_char ) {
		case 'A':
			return 'image-grid-card__stage--stage-a';
		case 'B':
			return 'image-grid-card__stage--stage-b';
		case 'C':
			return 'image-grid-card__stage--stage-c';
	}
}

function updateDatabaseTags( updated_database_options, select_option_id ) {
	emit( 'update-database-tags', updated_database_options, select_option_id );
}

</script>
